import {
	Breakpoints,
	Container,
	ContainerGutter,
	CTA,
	Layout,
	Text,
	TextAlignment,
	TokenTextAppearance,
} from '@volkswagen-onehub/components-core';
import { ChevronRight, NewCarPrivate } from '@volkswagen-onehub/icons-core';
import * as React from 'react';
import styled from 'styled-components';
import { DataVehicles, VehicleStatus } from '@esportal/portal-service';
import { CarAlertIcon } from './utils/car-alert-icon';
import { COLOR_TOKEN } from '../../helpers/color-tokens';
import { useVehicleManagerLayer } from './hooks/use-vehicle-manager-layer';
import { useAlertVehicleManagerLayer } from './hooks/use-alert-vehicle-manager-layer';

const VehicleToConfirmLayout = styled.div`
	position: fixed;
	bottom: 20px;
	width: 100%;
	z-index: 99;
`;

const VehicleToConfirmContainer = styled.div`
	background: white;
	box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
	border-radius: 10px;
	padding: 16px;
	gap: 3px;

	display: flex;
	align-items: center;
	justify-content: space-between;
`;

const MyVehicles = styled.div<{ notificationBarHeight: number }>`
	position: absolute;
	top: calc(var(--size-dynamic0130) + ${(props: { notificationBarHeight: number }) => props.notificationBarHeight}px);
	right: 0;
	width: fit-content;

	background: white;
	z-index: 1;
	display: inline-flex;
	align-items: center;
	border-radius: 8px 0 0 8px;
`;

const Blue600Container = styled(Text)`
	color: ${COLOR_TOKEN.button.primary};
`;

const IconStyled = styled.div`
	& svg {
		width: 24px;
		height: 24px;
	}
`;

const StyledContainer = styled.div`
	padding: 8px 18px 8px 12px;
`;

export const VehicleManager = ({
	userVehicles,
	notificationBarHeight,
}: {
	userVehicles: DataVehicles | null;
	notificationBarHeight: number;
}): React.ReactElement => {
	const { handleLayerOpen: handleAlertLayerOpen } = useAlertVehicleManagerLayer();
	const { handleLayerOpen: handleVehicleManagerLayerOpen, isLayerOpen: isVehicleManagerLayerOpen } =
		useVehicleManagerLayer({ onError: handleAlertLayerOpen });
	const [vehicles, setVehicles] = React.useState<DataVehicles | null>(userVehicles);

	React.useEffect(() => {
		if (vehicles != userVehicles) {
			setVehicles(userVehicles);
		}
	}, [userVehicles]);

	React.useEffect(() => {
		if (vehicles?.vehicles.some((vehicle) => vehicle.status === VehicleStatus.TO_BE_CONFIRMED)) {
			handleVehicleManagerLayerOpen();
		}
	}, [vehicles]);

	const hasVehiclesOrOrders = (vehicles?.vehicles?.length ?? 0) > 0 || (vehicles?.orders?.length ?? 0) > 0;

	return (
		<>
			{!isVehicleManagerLayerOpen && hasVehiclesOrOrders && (
				<MyVehicles
					onClick={handleVehicleManagerLayerOpen}
					style={{ cursor: 'pointer' }}
					notificationBarHeight={notificationBarHeight}
				>
					<StyledContainer>
						<Container wrap={'never'} gutter={ContainerGutter.static150} verticalAlign={'center'}>
							<IconStyled>
								<NewCarPrivate variant="default" aria-hidden />
							</IconStyled>

							{/* {isCTAVisible && ( */}
							<Container wrap={'never'} gutter={ContainerGutter.static150} verticalAlign={'center'}>
								<Blue600Container appearance={TokenTextAppearance.headline100} textAlign={TextAlignment.center} bold>
									Mis coches
								</Blue600Container>

								<CTA
									tag="button"
									emphasis="nbab"
									onClick={() => undefined}
									aria-label="Mis coches"
									size="small"
									icon={<ChevronRight variant="small" aria-hidden />}
								/>
							</Container>
							{/* )} */}
						</Container>
					</StyledContainer>
				</MyVehicles>
			)}

			{vehicles?.vehicles.some((vehicle) => vehicle.status === VehicleStatus.TO_BE_CONFIRMED) &&
				!isVehicleManagerLayerOpen && (
					<VehicleToConfirmLayout>
						<Layout
							data-testid="vehicle-manager-layout"
							appearance={{
								[Breakpoints.default]: [
									{ name: '.', columns: 1 },
									{ name: 'a', columns: 22 },
									{ name: '.', columns: 1 },
								],
								[Breakpoints.b560]: [
									{ name: '.', columns: 4 },
									{ name: 'a', columns: 16 },
									{ name: '.', columns: 4 },
								],
								[Breakpoints.b960]: [
									{ name: '.', columns: 5 },
									{ name: 'a', columns: 14 },
									{ name: '.', columns: 5 },
								],
								[Breakpoints.b1280]: [
									{ name: '.', columns: 7 },
									{ name: 'a', columns: 10 },
									{ name: '.', columns: 7 },
								],
								[Breakpoints.b1600]: [
									{ name: '.', columns: 8 },
									{ name: 'a', columns: 8 },
									{ name: '.', columns: 8 },
								],
								[Breakpoints.b2560]: [
									{ name: '.', columns: 9 },
									{ name: 'a', columns: 6 },
									{ name: '.', columns: 9 },
								],
							}}
							allowOverflowingContent
						>
							<VehicleToConfirmContainer className="vehicle-to-confirm-container">
								<Container wrap={'never'} verticalAlign={'center'} gutter={ContainerGutter.static200} shrinkContent>
									<IconStyled>
										<CarAlertIcon />
									</IconStyled>

									<Text appearance={TokenTextAppearance.label150} textAlign={TextAlignment.left}>
										Confirma, elimina o añade tu coche para dejar listo tu garaje.
									</Text>
								</Container>

								<CTA
									tag="button"
									emphasis="nbab"
									onClick={handleVehicleManagerLayerOpen}
									aria-label="Confirma, elimina o añade tu coche para dejar listo tu garaje."
									icon={<ChevronRight variant="default" aria-hidden />}
								/>
							</VehicleToConfirmContainer>
						</Layout>
					</VehicleToConfirmLayout>
				)}
		</>
	);
};
